<template>

  <v-layout class="inner-pages" column>
    <Header :header-height="$auth.check() ? '70px': '120px'" />

    <scroll-data>
      <v-flex class="pt-0">
        <h2 class="page-head"><span class="page-heading">User License & Terms of Use</span></h2>
        <div >
          <p>YOU ARE SIGNING UP FOR A COGNETI PROGRAM.</p>

          <p>
            BY CLICKING "I AGREE," YOU ACKNOWLEDGE THAT YOU HAVE READ AND AGREE THAT THE FOLLOWING TERMS AND CONDITIONS
            GOVERN YOUR USE OF THE PROGRAM MATERIALS, YOUR PROGRAM EXAMINATION, AND YOUR USE OF THE COGNETI CERTIFICATE
            AND eBADGE.
          </p>

          <p>
            Cogneti is accredited by the ANSI National Accreditation Board (ANAB) and carries
            <a href="https://www.ansi.org/Accreditation/credentialing/certificate-issuers/AllDirectoryDetails?&prgID=212&OrgId=2249&statusID=4">
              accreditation ID No. 1243</a>.
            Initial accreditation was granted on September 19, 2017, and is valid through September 2022.
            Our programs prepare you for successful employment and career growth.
          </p>

          <p>This Agreement is between you (the individual) and Cogneti, LLC, as described below.
            By accepting this Agreement, it is understood that you have read the Agreement and that you agree to abide by the Agreement.
          </p>

          <ul class="go-to-links">
            <li @click.prevent="goTo('validity_term')">Validity (Term) of the Cogneti eBadge</li>
            <li @click.prevent="goTo('cogneti_ebadge_use')">Cogneti eBadge Use, Program Use, and Program Confidentiality</li>
            <li @click.prevent="goTo('free_paid_content')">Free/Paid Content, Renewal, Cancellation, and Refund</li>
            <li @click.prevent="goTo('invalidating_certificate')">Invalidating a Certificate or a Badge</li>
            <li @click.prevent="goTo('privacy_and_the_use')">Privacy and the Use of Your Personal Information</li>
            <li @click.prevent="goTo('grievance_appeal_feedback')">Grievance, Appeal, Feedback, and Support Process</li>
            <li @click.prevent="goTo('americans_with_disabilities_act')">Americans with Disabilities Act (ADA)</li>
            <li @click.prevent="goTo('general_program_notification_and_nondiscrimination')">General Program Notification and Nondiscrimination</li>
            <li @click.prevent="goTo('definitions_of_terms_in_this_agreement')">Definitions of terms in this Agreement</li>
          </ul>

          <div class="mt-4">
            <h4 class="font-weight-bold mb-2" id="validity_term">Validity (Term) of the Cogneti eBadge</h4>
            The Cogneti program is an ANSI-accredited certificate program (
            <a target="_blank"
               href="https://www.ansi.org/accreditation/credentialing/certificate-issuers/AllDirectoryDetails?&prgID=212,238&OrgId=2249&statusID=4">
              Accreditation ID 1243</a>) that prepares you for
            successful employment and career growth. Cogneti's certificate and eBadge are valid from the date of issue
            and are credentialed by Cogneti Certificate Service. Each eBadge carries a unique user identification and
            can be verified as existing and active by using the <a href="https://admin.cogneti.com/badge" target="_blank">Cogneti website</a>.
          </div>


          <div class="mt-4">
            <h4 class="font-weight-bold mb-2" id="cogneti_ebadge_use">Cogneti eBadge Use, Program Use, and Program Confidentiality</h4>
              <ul>
                <li>
                  You may use the Cogneti certificate, eBadge, logo, and written confirmation as proof, or representation, of your successful completion of the Cogneti program for which you enrolled only in the manner it was approved and issued.
                </li>
                <li>
                  You agree to keep the contents of the Cogneti training program, the contents of the examination, and all other program communication with Cogneti confidential. Confidential information includes program content and exam questions and answers.
                </li>

                <li>
                  You may not use this program information for any reason other than to gain your personal Cogneti certificate and eBadge.
                </li>

                <li>
                  You will not give the rights of your certificate (s) to any other person.
                </li>

                <li>
                  You agree not to misrepresent the certificate or the eBadge or harm Cogneti, LLC's reputation or its program(s).
                </li>

                <li>
                  You agree to comply with all applicable federal, state, and local laws and codes in your service or business practice.
                </li>

                <li>
                  You understand that while the Cogneti certificate and eBadge may increase your chances of gainful employment, you will not hold Cogneti, LLC, their officers, directors, employees, agents, or successors, and assignees liable for any or all harm in the event of unemployment.
                </li>

                <li>
                  You agree that Cogneti, LLC, their officers, directors, employees, agents, or successor, and assignees are not liable for any direct or indirect, or special damage(s) you may cause to person or place of business during the performance of your duties while being a Cogneti certificate or eBadge holder.
                </li>

                <li>
                  All feedback you give Cogneti, LLC for its program will become the property of Cogneti, LLC, without payment, royalties, or other consideration to you.
                </li>

                <li>
                  This Agreement is governed by the country's laws where the examination is taken or, if different, the individual's country of residence taking the course(s). The courts of that country will have jurisdiction over matters.
                </li>

                <li>
                  You agree that this is a nonexclusive license to a specific Cogneti, LLC program. The program remains the licensor's property, who is the owner of all rights, titles, and interests in the software. No copies of the software will be made by yourself or others unless expressly authorized by Cogneti.
                </li>

                <li>
                  To the best of Cogneti's knowledge, this program does not infringe on any third-party's rights.
                </li>

                <li>
                  You may contact Cogneti at any time by submitting a support request via <a href="https://www.cogneti.com/contact/" target="_blank">Contact - Cogneti.</a>
                </li>
              </ul>
          </div>


          <div class="mt-4">
            <h4 class="font-weight-bold mb-2" id="free_paid_content">Free/Paid Content, Renewal, Cancellation, and Refund</h4>
            <ul>
              <li>
                Some program levels might be at no cost. The access to higher program levels and the program examination is premium content. Premium content requires payment before it is opened for your use. You will be prompted to enter payment information when accessing premium content.
              </li>
              <li>
                You may renew a Cogneti certificate or eBadge by completing all steps of the enrolled program again. The renewal process requires completing all course-level requirements to unlock a final exam. You will need to take and pass the final exam before a renewal certificate, or eBadge is issued.
              </li>
              <li>
                If after purchasing premium content, you wish to cancel the program or request a refund, you may do so by contacting Cogneti and including a statement of why you wish to cancel or receive a refund.
              </li>

            </ul>
          </div>

          <div class="mt-4">
            <h4 class="font-weight-bold mb-2" id="invalidating_certificate">Invalidating a Certificate or a Badge</h4>
            <ol>
              <li>If you are found to have violated any terms of this Agreement, you agree that all current certificates you hold will be deemed invalid. As a result, you will be permanently ineligible for any future Cogneti certificates. Additionally, Cogneti will remove all currently held certificates from our searchable verification database.
</li>
              <li>
                Cogneti will inform you by email of the invalidation of your certificate and eBadge.
</li>
              <li>
                Once invalidation notification from Cogneti is received, you must cease using the Cogneti certificate or eBadge, all associated logos, and any written acknowledgments.
</li>
              <li>
                If you believe Cogneti took this action in error, you may use our Grievance Process (listed below) by submitting a support ticket via <a href="https://www.cogneti.com/contact/" target="_blank">Contact - Cogneti.</a> </li>
            </ol>
          </div>

          <div class="mt-4">
            <h4 class="font-weight-bold mb-2" id="privacy_and_the_use">Privacy and the Use of Your Personal Information</h4>
            <ul>
              <li>You agree that Cogneti, LLC will collect, process, and use your personally identifying data to process and track payment for the program, the issuing of the Cogneti certificates or eBadges, tracking of individual exam scores, and your compliance with this Agreement.
</li>
              <li>
                In all other ways, Cogneti, LLC will protect the information that we hold. Please review our privacy policy at
                <a href="https://cogneti.com/home/privacy" target="_blank">https://cogneti.com/home/privacy</a>.
</li>
              <li>
                Contact Cogneti, LLC using <a href="https://www.cogneti.com/contact/" target="_blank">Contact - Cogneti.</a>  or send an email to
                <a href="mailto:support@cogneti.com">support@cogneti.com</a> if you wish to change your contact information.</li>
            </ul>
          </div>

          <div class="mt-4">
            <h4 class="font-weight-bold mb-2" id="grievance_appeal_feedback">Grievance, Appeal, Feedback, and Support Process</h4>
            <p>
              As part of the User License Agreement, you may contact Cogneti by submitting a support ticket via <a href="https://www.cogneti.com/contact/" target="_blank">Contact - Cogneti.</a> for any of the following purposes. All communication for support, feedback, grievance, or complaint is recorded and made available during periodic program reviews and audits conducted by an Advisory Group. Cogneti, LLC will attempt to provide an initial response to all support requests within 48 hours.
            </p>
            <ul>
              <li>Record a grievance or complaint about a Cogneti training program</li>
              <li>Request an appeal of an examination score or result</li>
              <li>Provide feedback on a Cogneti training program or the mobile applications</li>
                <li>Request support for service, information, or other action</li>
            </ul>
            <div class="my-3">
              You may initiate support requests using:
            </div>
            <ul>
              <li>The Feedback & Support function within the Cogneti mobile app on your connected device</li>

              <li>Clicking "Contact" on the <a href="https://www.cogneti.com/contact/" target="_blank">Cogneti.com</a> website</li>

              <li>Sending an email to <a href="mailto:support@cogneti.com">support@cogneti.com</a></li>
            </ul>
            <p class="mt-3">
              Cogneti, LLC shall identify all such communications with a unique tracking number and categorize them for action as a grievance, appeal, feedback, or support request.
            </p>
            <p>
              Grievances or appeals must be submitted through the app or email within 14 business days from the date of the event. Cogneti, LLC will respond via email within five business days with our decision. If you would like to appeal further, you may submit an appeal of our decision within 14 business days. We will review your appeal within five additional business days and issue a written final and binding decision. Cogneti will not hear additional appeals after our final and binding decision.
            </p>
          </div>

          <div class="mt-4">
            <h4 class="font-weight-bold mb-2" id="americans_with_disabilities_act">Americans with Disabilities Act (ADA)</h4>
            <p>
              Cogneti, LLC follows all applicable provisions of the Americans With Disabilities Act (ADA) and will not discriminate against any individual applying to take our program due to a disability as defined by the ADA. Cogneti, LLC will provide reasonable accommodations to a person with a disability (defined by the ADA) who has informed us of that disability, provided that any accommodations will not constitute an undue hardship to the company.
            </p>
            <p>
              If special accommodation is required, you should contact Cogneti at <a href="mailto:support@cogneti.com">support@cogneti.com</a> or <a href="https://www.cogneti.com/contact/" target="_blank">Cogneti.com</a>. We will consider all factors, including, but not limited to, the cost and nature of accommodations, available tax credits, deductions, or outside funding, and decide if we can provide the requested accommodations. Cogneti, LLC will inform you of their decision on the request. If we deny your request, you may enter a written appeal that includes why the accommodations are necessary. We will review the request, and if the appeal is denied, that decision is final.
            </p>
          </div>

          <div class="mt-4">
            <h4 class="font-weight-bold mb-2" id="general_program_notification_and_nondiscrimination">General Program Notification and Nondiscrimination</h4>
            <ul>
              <li>Cogneti, LLC does not receive any commercial or third-party support in creating certificate training programs. The awarding of a certificate is based solely on the student's performance.</li>

              <li>
                The program content is up to date with all federal and state regulations and industry best-practices standards at the time of publication. In addition, all attempts are made to keep the program current with ongoing changes in regulations.
</li>
              <li>
                Our program is open and available to all persons worldwide. Cogneti, LLC follows the law in policies that separate or exclude individuals due to their race, color, religion, national origin, disability, age, sex, sexual preference, or any other protected category. </li>
            </ul>
          </div>

          <div class="mt-4">
            <h4 class="font-weight-bold mb-2" id="definitions_of_terms_in_this_agreement">Definitions of terms in this Agreement</h4>
            <p>
              <span class="font-weight-bold">Accreditation Authority</span>—is the nationally or internationally acknowledged agency with oversight and decision-making power of the industry.
            </p>
            <p>
              <span class="font-weight-bold">ANSI National Accreditation Board (ANAB)</span>—A nationally and internationally recognized nonprofit organization that oversees standards for products, services, personnel, and systems in the United States.
            </p>
            <p>
              <span class="font-weight-bold">Certificate</span>—and its associated logo and the written acknowledgment show that you (the individual) have successfully passed the final exam for a Cogneti training program. The certificate is an acknowledgment that you have passed only the certificate program for which you have been successfully enrolled and for which you have passed the exam. Cogneti, LLC may add additional Cogneti certificate programs from time to time.
            </p>
            <p>
              <span class="font-weight-bold">Certificate Examination (Final Exam)</span>—is the test of skills or recall, which is a requirement for completing a program and receiving a certificate or eBadge, both of which acknowledge your completion.
            </p>
            <p>
              <span class="font-weight-bold">Cogneti Certified eBadge Holder</span>—is the individual (you) who has successfully passed the exam for the Cogneti certificate program.
            </p>
            <p>
              <span class="font-weight-bold">Confidential information</span> is any information contained in or related to the Cogneti program(s) or exam(s). It also includes any communication which has not been made available to the general public. For example, confidential information includes program content and exam questions and answers. However, it does not include information available to the general public or the information you developed independently (by yourself) as long as it does not include any Cogneti confidential information.

            </p>
            <p>
              <span class="font-weight-bold">eBadge</span>—is the visual representation of a user who has completed a Cogneti training program by receiving a passing grade on the course final exam. The eBadge may be received as a laminated, credit card-sized photo identification for an additional fee. Badges capable of user printing will be emailed to the user on the satisfaction of all program requirements.

            </p>
            <p>
              <span class="font-weight-bold">Logo(s)</span>—are the Cogneti, LLC image(s) used in the program, exam, and eBadge. You agree not to use the logos in any way that Cogneti, LLC, did not intend. From time to time, Cogneti may revise the artwork at its sole discretion and will post the most current version at
              <a href="https://www.cogneti.com/" target="_blank">www.cogneti.com</a>.

            </p>
            <p>
              <span class="font-weight-bold">Premium Content (Paid Content)</span>—some Cogneti programs may offer limited levels of training content free of charge but will require payment before unlocking premium content.  Premium content will include the program's certificate final examination. Purchase of premium content allows the user to continue access to the program for continued practice or training.

            </p>
            <p>
              <span class="font-weight-bold">Program</span>—is the Cogneti, LLC training program described and governed by this Agreement for which you are enrolling. The full program includes all levels of study content, program quizzes, final exam, and any games or other material released as part of or in conjunction with the study content.

            </p>
            <p>
              <span class="font-weight-bold">Renewal</span>—is the process by which a Cogneti certificate or eBadge is extended for a new period (term of validity). You may renew a course certificate or eBadge by completing all steps of the program again, including reviewing all levels of instruction, completing quizzes, and successfully completing a final exam.

            </p>
            <p>
              <span class="font-weight-bold">The Term</span>—is the amount of time that a Cogneti certificate remains valid. The validity of certificates and eBadges will vary by the program in which you enroll. All Cogneti certificates and eBadges are valid starting on the date of issue.

            </p>
          </div>
        </div>
      </v-flex>
    </scroll-data>

  </v-layout>
</template>

<script>
  import ScrollData from '../../ScrollData'
  import Header from '../../layouts/Header'
  export default {
    name: 'TermsOfUse',
    components: {Header, ScrollData},
    data () {
      return {
        appName: process.env.APP_NAME,
        offsetTop: 0,
        scrollHeight: 0,
        scrolled: 0
      }
    },
    created () {
      window.addEventListener('scroll', this.handleScroll)
    },
    destroyed () {
      window.removeEventListener('scroll', this.handleScroll)
    },
    mounted () {
      this.scrollHeight = this.$store.getters.getAppHeight - 190
    },
    methods: {
      goTo (elem) {
        const element = document.getElementById(elem)
        element.scrollIntoView({behavior: 'smooth'})
      },
      handleScroll () {
        this.scrolled = window.scrollY
      },
      onScroll (e) {
        const top = e.target.scrollTop
        if (top > 300) {
          this.header = false
          this.appHeight = this.$store.getters.getAppHeight
        } else {
          this.header = true
          this.appHeight = this.$store.getters.getAppHeight - 190
        }
      }
    }
  }
</script>
<style lang="stylus">
@import '~vuetify/src/stylus/main.styl';
ol ol {
  list-style: lower-alpha;
}
ul.go-to-links > li{
  color: $blue.darken-1;
  cursor: pointer;
}
  h4 {
    font-size: 16px;
  }
</style>



// WEBPACK FOOTER //
// src/components/views/guest/TermsOfUse.vue